<template>
    <div class="ingestion-component">
        <card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title">Ingestion Queue</h1>

                    <div>
                        <b-form-group>
                            <b-form-radio-group v-model="selectedView" :options="selectViewOptions"
                                button-variant="primary" buttons />
                        </b-form-group>
                    </div>
                </div>
            </template>

            <b-row v-if="selectedView === 'Incomplete'">
                <b-col>
                    <h5>Upload Documents</h5>
                    <vue-dropzone id="dropzone" ref="myVueDropzone" :options="dropzoneOptions"
                        @vdropzone-success="dropzoneUploadSuccess" @vdropzone-sending="dropzoneSending" />
                </b-col>
                <b-col v-if='scanners.length > 0'>
                    <h5>Scan Documents</h5>
                    <label>Select Scanner:</label>
                    <b-select v-model="selectedScanner" :options="scanners"></b-select>
                    <div style="display: flex; align-items:center">
                        <b-button class="mb-0" variant="primary" :disabled="isScanning" @click="scan">Scan
                            Documents</b-button>
                        <i v-if="isScanning" class="fas fa-spinner fa-spin fa-2x ml-2"></i>
                    </div>
                </b-col>
            </b-row>

            <div>
                <div class="action-bar glass-pane">
                    <b-row align-v="end" class="h-100 mt-2">
                        <b-col cols="3">
                            <b-form-group label="Move To:">
                                <b-form-select v-model="destinationBucket" :options="bucketOptions"
                                    class=" align-middle" size="sm" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="2">
                            <b-form-group label="">
                                <b-button class="mb-0" variant="primary" size="sm" @click="routeItems">
                                    Move Items
                                </b-button>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Currently Viewing:">
                                <b-form-select v-model="selectedBucket" :options="bucketOptions" size="sm" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Queue Type:">
                                <b-form-select v-model="selectedType" :options="typeOptions" size="sm" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="2">
                            <checkbox v-show="canIncludeLocked" v-model="allowLocked" style="margin-top:-40px;">Include
                                Locked
                            </checkbox>
                        </b-col>
                    </b-row>
                    <b-row v-if="selectedType" align-v="end" class="py-2">
                        <b-col cols="5">
                            <b-form-group v-if="selectedView === 'Complete'" label="Completed Date:">
                                <el-date-picker v-model="dateRange" type="daterange" range-separator="To" start-placeholder="Start date" end-placeholder="End date" :picker-options="datePickerOptions" :clearable="false" />
                            </b-form-group>
                            <b-form-group v-else>
                                <label>
                                    Search:
                                </label>
                                <HelpPopup>
                                    <div>
                                        <p><strong>Search the ingestion queue using any of the following:</strong></p>
                                        <h6>Phone Numbers</h6>
                                        <ul>
                                            <li>Format: <code>+15551234567</code> or <code>15551234567</code></li>
                                            <li>Searches: Source, Patient phone, Prescriber phone</li>
                                        </ul>
                                        <h6>Dates</h6>
                                        <ul>
                                            <li>Format: <code>MM/DD/YYYY</code> or <code>MM/DD/YY</code></li>
                                            <li>Searches: Patient date of birth, Medication written date</li>
                                            <li>Example: <code>01/15/1990</code></li>
                                        </ul>
                                        <h6>Names</h6>
                                        <ul>
                                            <li>Simple search: <code>Smith</code> or <code>John</code></li>
                                            <li>Comma-separated: <code>Smith, John</code></li>
                                            <li>Searches: Patient first/last name, Prescriber first/last name</li>
                                        </ul>
                                    </div>
                                </HelpPopup>
                                <b-form-input v-model="searchValue" type="search" debounce="500"
                                    placeholder="Search..." />
                            </b-form-group>
                        </b-col>
                        <b-col></b-col>
                        <b-col>
                            <SearchComponent v-show="selectedType === 'escript'" v-model="store" label="Store"
                                search-u-r-l="/Store" :params="{active: true}" prepopulate clearable size="sm" />
                        </b-col>
                    </b-row>
                </div>
                <template v-if="selectedType">
                    <b-table id="escript-queue" ref="escriptTable" v-model="tableItems" primary-key="id"
                        api-url="IngestionQueue" height="20rem" empty-text="Queue is empty..."
                        empty-filtered-text="No items in the selected bucket." :items="tableProvider" :fields="fields"
                        :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" :filter="tableFilter"
                        :tbody-transition-props="transProps" :tbody-tr-class="handleQueueRowStyles" outlined
                        sort-icon-left show-empty @row-clicked="handleQueueRowClicked">
                        <template #cell(selected)="data">
                            <Checkbox :checked="itemsSelected.some(i => i === data.item)"
                                @input="handleItemSelect(data.item, $event)" />
                        </template>
                        <template #cell(patient)="data">
                            {{ patientFormatter(data.item.patient) }}
                        </template>
                        <template #cell(imgSrc)="data">
                            <ingestion-image :key="data.item.id" v-model="data.item" />
                        </template>
                        <template #cell(buttons)="data">
                            <template v-if="!data.item.completed">
                                <b-button class="my-1" block variant="primary" :disabled="!!itemsSelected.length"
                                    @click="processQueueItem(data.item)">
                                    Process Item
                                </b-button>
                                <b-button class="my-1" block variant="danger" :disabled="!!itemsSelected.length"
                                    @click="postNotScript([data.item])">
                                    Not A Script
                                </b-button>
                                <b-button class="my-1" block variant="success" :disabled="!!itemsSelected.length"
                                    @click="setPatientDocument(data.item)">
                                    Patient Document
                                </b-button>
                                <b-button class="my-1" block
                                    :variant="data.item.isUrgent ? 'warning' : 'outline-warning'"
                                    :style="data.item.isUrgent ? 'font-weight: bold' : ''"
                                    :disabled="!!itemsSelected.length"
                                    @click="toggleUrgent(data.item)">
                                    <i :class="data.item.isUrgent ? 'fas fa-check-square' : 'far fa-square'"></i>
                                    Urgent
                                </b-button>
                            </template>
                            <template v-if="data.item.completed">
                                <b-button v-if="data.item.rxNumber"
                                    :href="`/processing/prescription/${data.item.storeID}/${data.item.rxNumber}/${data.item.rfNumber ?? 0}`"
                                    class="my-1" variant="outline-primary" block>
                                    View Script<br>
                                    <small>
                                        <strong>
                                            {{
                                                `${data.item.storeID}-${data.item.rxNumber}-${data.item.rfNumber ?? 0}`
                                            }}
                                        </strong>
                                    </small>
                                </b-button>
                                <b-button v-else-if="data.item.patientId"
                                    :href="`/processing/patient-dashboard/${data.item.patientId}`" class="my-1"
                                    variant="outline-primary" block>
                                    View Patient
                                </b-button>
                                <template v-else>
                                    <b-button class="my-1" block variant="primary" @click="processQueueItem(data.item)">
                                        Process Item
                                    </b-button>
                                    <b-button class="my-1" block variant="success"
                                        @click="setPatientDocument(data.item)">
                                        Patient Document
                                    </b-button>
                                </template>
                            </template>
                        </template>
                        <template #cell(locked)="data">
                            {{ data.item.lockedBy }}
                            <div v-if="data.item.lockExpires">
                                Lock Expires {{ formatLockExpires(data.item.lockExpires) }}
                            </div>
                        </template>
                        <template #cell(completed)="data">
                            {{ data.item.completedBy }}
                            <div>
                                {{ data.value | formatDate("LLL") }}
                            </div>
                        </template>
                    </b-table>

                    <BottomPagerBar v-model="pageNumber" :hide-pager="loading" :total-rows="itemCount"
                        :per-page="pageSize" show-total show-page-size-selector :loading="loading"
                        :selected-rows="itemsSelected.length" @change-page-size="pageSize = $event"
                        @clear-selection="itemsSelected = []">
                        <template v-if="selectedView === 'Incomplete' && itemsSelected.length" #fab>
                            <b-button variant="primary" @click="processQueueItem(itemsSelected[0])">
                                <strong>
                                    Process {{ itemsSelected.length === 1 ? "Item" : "Items" }}
                                </strong>
                            </b-button>
                            <b-button variant="warning" @click="selectAllForPatients()">
                                Select All for {{ itemsSelected.length === 1 ? 'Patient' : 'Patients' }}
                            </b-button>
                            <b-button variant="danger" @click="postNotScript(itemsSelected)">
                                Mark {{ itemsSelected.length === 1 ? "Item" : "Items" }} As Not A Script
                            </b-button>
                            <b-button variant="success" @click="setPatientDocument(itemsSelected)">
                                Mark {{ itemsSelected.length === 1 ? "Item" : "Items" }} As Patient Document
                            </b-button>
                        </template>
                    </BottomPagerBar>
                </template>
                <div v-else class="text-center p-5">
                    Select a queue type to continue.
                </div>

            </div>
            <PatientDocumentsModal :queue-item="selectedQueueItem" @on-save="handlePatientDocSave" />
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator';
import Axios, { AxiosRequestConfig } from 'axios';
import Vue2Dropzone from 'vue2-dropzone';
import moment from "moment";

import { BTable, BvTableCtxObject } from "bootstrap-vue";
import SearchComponent from "@/components/SearchComponent.vue";
import PatientDocumentsModal from '@/components/Patient/PatientDocumentsModal.vue';

import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { LockHandler } from '@/mixins/LockHandler';
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import { Prescriber } from '@/models/Prescriber';
import { QueueItem } from '@/models/QueueItem';
import { Program } from '@/models/Program';
import { Patient } from '@/models/Patient';
import { Store } from "@/models/Store";
import IngestionImage from "@/components/IngestionImage.vue";
import Dropzone from "dropzone";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { DatePicker } from 'element-ui';
import BottomPagerBar from './BottomPagerBar.vue';
import { EntityType } from '@/models/EntityType';
import HelpPopup from './HelpPopup.vue';

@Component({
    name: "IngestionComponent",
    components: {
        "VueDropzone": Vue2Dropzone,
        SearchComponent,
        PatientDocumentsModal,
        IngestionImage,
        Checkbox,
        [DatePicker.name]: DatePicker,
        BottomPagerBar,
        HelpPopup
    },
})
export default class IngestionComponent extends Mixins(LockHandler, AutoModeHotkeyMixin) {
    private itemsSelected: Array<QueueItem> = [];
    private programs: Array<Program> = [];
    private destinationBucket: any = null;
    private selectedBucket: any = null;
    private store: Store = new Store();
    private scanners: Array<string> = [];
    private selectedScanner: string = "";
    private isScanning: boolean = false;
    @Ref("myVueDropzone") private dropzone!: Dropzone;
    @Ref("escriptTable") private escriptTable!: BTable;
    private dateRange: Date[] = [new Date(), new Date()];
    private firstSelectedDate: Date | null = null;  // used for datepicker
    private pageNumber = 1;
    private pageSize = this.persistentPageSize || 100;
    private loading = true;
    private workerMessageHandler: ((e: MessageEvent) => void) | null = null;

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
        this.selectedType = this.persistentType || "all";
        this.fetchPrograms();
        this.interval = setInterval(this.fetchQueue, 600_000);
        this.setupWorkerConnection();
        this.getAvailableScanners();
        this.allowLocked = !!this.$route.query["allowLocked"];

        const routeId = this.$route.params.id;
        if (routeId) {
            this.loadSingleItem(parseInt(routeId));
        }
    }

    async loadSingleItem(id: number) {
        try {
            const response = await Axios.get<QueueItem>(`/IngestionQueue/${id}`);
            const item = response.data;

            if (item) {
                this.processQueueItem(item);
            }
        } catch (error) {
            console.error('Error loading single ingestion item:', error);
            this.$notification(NotificationOptions.error('Failed to load queue item'));
        }
    }

    getAvailableScanners() {
        // Try to use hub first if worker is available
        if (this.$workerPort) {
            console.log("Requesting scanners from client service hub via worker");
            this.$workerPort.postMessage(['clientServiceRefreshScanners', null]);
            // No timeout needed - worker will immediately respond with empty array if not connected
        } else {
            // Fallback to HTTP immediately if no worker
            this.getAvailableScannersFallback();
        }
    }

    getAvailableScannersFallback() {
        Axios.get('http://localhost:7274/scanner').then((result) => {
            this.scanners = result.data.map((s: any) => s.name);
            const previouslySelectedScanner = this.persistentScanner;
            if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                this.selectedScanner = previouslySelectedScanner;
            }
        }).catch((err: any) => {
            console.warn(err);
        });
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
        clearInterval(this.interval);
        // Remove message handler
        if (this.workerMessageHandler && this.$workerPort) {
            this.$workerPort.removeEventListener('message', this.workerMessageHandler);
        }
    }

    setupWorkerConnection() {
        if (!window.SharedWorker || !this.$workerPort) {
            console.log("SharedWorker not available, will use HTTP fallback");
            return;
        }

        // Create message handler
        this.workerMessageHandler = (e: MessageEvent) => {
            const [messageType, data] = e.data;

            switch (messageType) {
                case "clientServiceScanners":
                    console.log("Received scanners from client service hub", data);
                    if (Array.isArray(data)) {
                        this.scanners = data.map((s: any) => s.name || s.id);
                        const previouslySelectedScanner = this.persistentScanner;
                        if (this.scanners.indexOf(previouslySelectedScanner) >= 0) {
                            this.selectedScanner = previouslySelectedScanner;
                        }
                    }
                    break;
                case "clientServiceScanResponse":
                    console.log("Received scan response from client service hub", data);
                    this.isScanning = false;
                    if (data.error) {
                        this.$notification(NotificationOptions.error(data.error));
                    } else {
                        // Convert response to blob and upload
                        let blob;
                        if (data instanceof Blob) {
                            blob = data;
                        } else if (data instanceof ArrayBuffer) {
                            blob = new Blob([data], { type: 'application/pdf' });
                        } else if (Array.isArray(data)) {
                            blob = new Blob([new Uint8Array(data)], { type: 'application/pdf' });
                        } else if (typeof data === 'string') {
                            // Handle base64 string
                            const binaryString = atob(data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                                bytes[i] = binaryString.charCodeAt(i);
                            }
                            blob = new Blob([bytes], { type: 'application/pdf' });
                        } else {
                            console.error("Unexpected scan response format", data);
                            this.$notification(NotificationOptions.error("Unexpected scan response format"));
                            return;
                        }
                        const formData = new FormData();
                        formData.append("images", blob, "scan.pdf");
                        const imageConfig = { headers: { "Content-Type": "multipart/form-data" } };
                        Axios.post('/image', formData, imageConfig)
                            .then(_response => {
                                this.fetchQueue();
                            })
                            .catch(error => {
                                console.log(error);
                                this.$notification(NotificationOptions.error(error));
                            });
                    }
                    break;
            }
        };

        // Add message handler
        this.$workerPort.addEventListener('message', this.workerMessageHandler);
    }

    get canIncludeLocked() {
        return this.$permissions.some(p => p.permissionCodeName === "Processing/Rx Intake Queue/View Locked");
    }

    get persistentType(): any {
        return localStorage.getItem('ingestion_selectedType');
    }

    get persistentBucket(): any {
        const objStr = localStorage.getItem('ingestion_selectedBucket');
        if (objStr)
            return JSON.parse(objStr);
        else return null;
    }

    get persistentScanner(): any {
        return localStorage.getItem('ingestion_selected_scanner');
    }

    get persistentPageSize(): any {
        const size = localStorage.getItem('ingestion_pageSize');
        return size ? parseInt(size) : null;
    }

    @Watch('selectedType')
    selectedTypeChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_selectedType', val1);
    }

    @Watch('selectedBucket')
    selectedBucketChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_selectedBucket', JSON.stringify(val1));
    }

    @Watch('selectedScanner')
    selectedScannerChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_selected_scanner', val1);
    }

    @Watch('pageSize')
    pageSizeChanged(val1: any, _val2: any) {
        localStorage.setItem('ingestion_pageSize', val1.toString());
    }

    @Watch('store')
    @Watch('selectedType')
    @Watch('pageSize')
    @Watch('dateRange')
    @Watch('selectedBucket')
    @Watch('allowLocked')
    resetPageNumber() {
        this.pageNumber = 1;
    }

    private interval!: NodeJS.Timeout;
    // dropzone
    // properties
    get dropzoneOptions(): any {
        return {
            url: `${Axios.defaults.baseURL}/image`,
            thumbnailWidth: 150,
            withCredentials: true,
            paramName: 'images',
        };
    }

    // events
    dropzoneSending(file: any, xhr: XMLHttpRequest, _formData: FormData) {
        const url = new URL(xhr.responseURL || `${Axios.defaults.baseURL}/image`);
        if(this.selectedBucket){
            url.searchParams.set('bucketType', this.selectedBucket?.bucketType);
            url.searchParams.set('bucket', this.selectedBucket?.bucket);
        }

        xhr.open('POST', url.toString());
        xhr.setRequestHeader("Authorization", Axios.defaults.headers["Authorization"]?.toString() ?? "");
    }

    dropzoneUploadSuccess(file: any, _response: any) {
        this.dropzone.removeFile(file);
        // const dropzone: any = this.$refs.myVueDropzone;
        // dropzone.removeFile(file);

        this.fetchQueue();
    }

    dateTimeFormatter(d: any) {
        return moment.utc(d).local().format("L LT");
    }

    // table
    // properties
    get escriptFields(): any[] {
        return [
            { key: "selected", label: "", class: "selected-col", },
            { key: "ingested", label: "Received", formatter: this.dateTimeFormatter, sortable: true, class: "date-col", },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "prescriber", label: "Prescriber", formatter: this.prescriberFormatter },
            { key: "drugs.drugDescription", label: "Drug", sortable: true },
            { key: "prescription.memo", label: "Memo", },
            //{key: "source", class: "source-col"},
            {
                key: "otherType",
                label: "Type",
                sortable: true,
                formatter: this.typeFormatter,
                sortByFormatted: true, /*class: "type-col",*/
            },
            { key: "completed", label: "Completed By", class: "completed", hidden: !this.isCompleteView },
            { key: "locked", label: "Locked By", class: "locked", hidden: !this.allowLocked },
            { key: "buttons", label: "", class: "button-col", },
        ];
    }

    get otherFields() {
        return [
            { key: "selected", label: "", class: "selected-col", },
            { key: "ingested", label: "Received", formatter: this.dateTimeFormatter, sortable: true, class: "date-col", },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "imgSrc", label: "Image", class: "image-col", },
            {
                key: "otherType",
                label: "Type",
                sortable: true,
                formatter: this.typeFormatter,
                class: "type-col",
            },
            { key: "source", class: "source-col" },
            { key: "completed", label: "Completed By", class: "completed", hidden: !this.isCompleteView },
            { key: "locked", label: "Locked By", class: "locked", hidden: !this.allowLocked },
            { key: "buttons", label: "", class: "button-col", },
        ];
    }

    get programTransferFields() {
        return [
            { key: "selected", label: "", class: "selected-col", },
            { key: "ingested", label: "Received", formatter: this.dateTimeFormatter, sortable: true, class: "date-col", },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "prescriber", label: "Prescriber", formatter: this.prescriberFormatter },
            { key: "prescription", label: "Drug", formatter: this.transferDrugFormatter },
            { key: "sendingPharmacy.name", label: "Source", class: "source-col", sortable: true, },
            { key: "completed", label: "Completed By", class: "completed", hidden: !this.isCompleteView },
            { key: "locked", label: "Locked By", class: "locked", hidden: !this.allowLocked },
            { key: "buttons", label: "", class: "button-col", },
        ];
    }

    protected sortBy = "ingested";
    protected sortDesc = false;
    protected tableItems: QueueItem[] = [];
    protected transProps = { name: "flip-list" };
    private selectedType = "other";
    private searchValue = "";
    private allowLocked = false;
    private selectedView = "Incomplete";
    private itemCount: number | null = null;

    protected selectedQueueItem: QueueItem | null = null;

    get isCompleteView() {
        return this.selectedView === "Complete";
    }

    get tableFilter() {
        const filter = {
            url: "",
            searchText: this.isCompleteView ? null : this.searchValue,
            type: this.selectedType,
            storeId: this.store?.id,
            allowLocked: this.allowLocked,
            complete: this.isCompleteView,
            dateRange: this.isCompleteView ? this.dateRange : null,
            pageSize: this.pageSize,
            pageNumber: this.pageNumber,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc
        };
        if (this.selectedBucket) {
            filter.url = `/${this.selectedBucket.bucketType}/${this.selectedBucket.bucket}`;
        }
        return filter;
    }

    get allItemsFields() {
        return [
            { key: "selected", label: "", class: "selected-col", },
            { key: "ingested", label: "Received", formatter: this.dateTimeFormatter, sortable: true, class: "date-col", },
            { key: "patient", label: "Patient", formatter: this.patientFormatter, sortable: true },
            { key: "imgSrc", label: "Image", class: "image-col", },
            { key: "prescriber", label: "Prescriber", formatter: this.prescriberFormatter },
            { key: "drugs.drugDescription", label: "Drug", sortable: true },
            {
                key: "typeAndSource",
                label: "Type / Source",
                formatter: this.typeAndSourceFormatter,
                sortable: true,
                class: "type-col"
            },
            { key: "completed", label: "Completed By", class: "completed", hidden: !this.isCompleteView },
            { key: "locked", label: "Locked By", class: "locked", hidden: !this.allowLocked },
            { key: "buttons", label: "", class: "button-col", },
        ];
    }

    get fields() {
        let fields: Array<any>;

        switch (this.selectedType) {
            case "all":
                fields = this.allItemsFields;
                break;
            case "escript":
                fields = this.escriptFields;
                break;
            case "programTransfer":
                fields = this.programTransferFields;
                break;
            default:
                fields = this.otherFields;
                break;
        }

        return fields.filter(f => f.hidden !== true);
    }

    tableProvider(ctx: BvTableCtxObject, callback: Function) {
        const filter = ctx.filter as unknown as {
            url: string,
            searchText: string | null,
            type: string,
            storeId: any,
            allowLocked: boolean,
            complete: boolean,
            dateRange: Date[] | null,
            pageNumber: number | null,
            pageSize: number | null,
            sortBy: string,
            sortDesc: boolean
        };

        this.loading = true;

        if (!filter.type) {
            callback([]);
            return null;
        }

        const uri = ctx.apiUrl + filter.url;
        const config: AxiosRequestConfig = {
            params: {
                allowLocked: filter.allowLocked,
                searchText: filter.searchText,
                queueType: filter.type,
                storeId: filter.storeId,
                complete: filter.complete,
                startDate: filter.dateRange && filter.dateRange.length > 0 ? filter.dateRange[0] : null,
                endDate: filter.dateRange && filter.dateRange.length > 1 ? filter.dateRange[1] : null,
                pageNumber: filter.pageNumber,
                pageSize: filter.pageSize,
                sortBy: filter.sortBy,
                sortDesc: filter.sortDesc
            },
        };
        Axios.get<{ queueItems: QueueItem[], count: number }>(uri, config)
            .then(resp => {
                const dat = resp.data;
                // Set image URLs without fetching - let IngestionImage component handle lazy loading
                dat.queueItems.forEach(item => {
                    if (this.selectedType !== "escript" && this.selectedType !== "programTransfer") {
                        if (this.selectedType !== "all" || item.imageID) {
                            item.imageUrl = this.imageUrlForQueueItem(item);
                        }
                    }
                });
                this.itemsSelected = [];
                this.itemCount = dat.count;  // Use total count for pagination, not just current page length
                callback(dat.queueItems);
            })
            .catch(err => {
                this.itemsSelected = [];
                console.error("Error while loading items for ingestion.", { err, response: err.response });
                this.$notification(NotificationOptions.error(err));
                callback([]);
            })
            .finally(() => this.loading = false);

        return null;
    }

    // formatters
    patientFormatter(pt: Patient) {
        return Object.assign(new Patient(), pt).displayNamesForPerson();
    }

    prescriberFormatter(dr: Prescriber) {
        return Object.assign(new Prescriber(), dr).displayNamesForPerson();
    }

    transferDrugFormatter(rxVM: any) {
        // noinspection JSUnresolvedReference
        return `${rxVM?.prescribedNdc} - ${rxVM?.medicationName}`;
    }

    typeFormatter(_value: null, _key: string, item: QueueItem) {
        if (item?.source === "escript") return item?.escriptMessageType;
        const type = item?.escriptMessageType;
        if (type) return type;
        if (item?.source === "programTransfer") return item.escriptMessageKind;
        if (!isNaN(Number(item?.source))) return "Fax";
        if (item?.source.startsWith("+")) return "Fax";

        return "Unknown";
    }

    typeAndSourceFormatter(_value: null, _key: string, item: QueueItem) {
        const queueType = item.queueType || "Unknown";
        const source = item.source || "";

        if (source && queueType.toLowerCase().replace(/\s/g, '') !== source.toLowerCase().replace(/\s/g, '')) {
            return `${queueType} - ${source}`;
        }
        return queueType;
    }

    imageUrlForQueueItem(item: QueueItem): string {
        if (item.imageID != null) {
            return `/image/${item.imageID}`;
        }
        if (item.escriptID != null) {
            return `/escript/${item.escriptID}/image`;
        }
        return '';
    }

    async fetchPrograms() {
        try {
            const resp = await Axios.get<Program[]>('/Program?active=true');
            this.programs = resp.data;
            this.loadBucketOptions();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    fetchQueue() {
        this.itemsSelected = [];
        if (this.escriptTable?.refresh) this.escriptTable.refresh();
    }

    routeItem(item: QueueItem) {
        item.bucketType = this.destinationBucket.bucketType;
        item.bucket = this.destinationBucket.bucket;
        return Axios.post("IngestionQueue/Route", item);
    }

    routeItems() {
        if (!this.$permissions.some(p => p.permissionCodeName === "Processing/Rx Intake Queue/Move Item")) {
            this.$notification(NotificationOptions.error("You cannot move items. Missing permission Processing/Rx Intake Queue/Move Item."));
            return;
        }

        Promise.all(this.itemsSelected.map(this.routeItem))
            .catch(err => this.$notification(NotificationOptions.error(err)))
            .finally(this.fetchQueue);
    }

    scan() {
        if (this.selectedScanner == "") {
            this.$notification(NotificationOptions.error("Please select a scanner."));
            return;
        }
        this.isScanning = true;

        // Try to use hub first if worker is available
        if (this.$workerPort) {
            console.log("Requesting scan from client service hub via worker");
            this.$workerPort.postMessage(['clientServiceScan', this.selectedScanner]);
            // No timeout needed - worker will immediately respond with error if not connected
        } else {
            // Fallback to HTTP immediately if no worker
            this.scanFallback();
        }
    }

    scanFallback() {
        const scanConfig: AxiosRequestConfig = { params: { scannerId: this.selectedScanner }, responseType: 'blob' };
        Axios.get('http://localhost:7274/scanner/scan', scanConfig)
            .then(result => {
                const formData = new FormData();
                formData.append("images", result.data);
                const imageConfig = { headers: { "Content-Type": "multipart/form-data" } };
                Axios.post('/image', formData, imageConfig)
                    .then(_response => {
                        this.fetchQueue();
                    });
            })
            .catch(error => {
                console.log(error);
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isScanning = false;
            });
    }

    get typeOptions() {
        return [
            { value: null, text: "Select Queue Type" },
            { value: "all", text: "All Items" },
            { value: "escript", text: "Escript" },
            { value: "programTransfer", text: "ProgramTransfer" },
            { value: "other", text: "Images" },
        ];
    }

    protected bucketOptions: Array<any> = [];

    loadBucketOptions() {
        const programOptions = this.programs.map(p => {
            return { value: { bucketType: 1, bucket: p.id }, text: p.name };
        });

        const staticOptions = [
            { value: { bucketType: 0, bucket: 0 }, text: 'Prior Auth' },
            { value: { bucketType: 0, bucket: 1 }, text: 'Audit' },
            { value: { bucketType: 0, bucket: 2 }, text: 'MD Responses' },
            { value: { bucketType: 0, bucket: 3 }, text: 'Misc' }
        ];

        this.bucketOptions = [
            { value: null, text: 'Unsorted Bucket' },
            { label: 'Programs', options: programOptions },
            { label: 'Other', options: staticOptions }
        ];

        this.selectedBucket = this.persistentBucket;
    }

    async postNotScript(queueItem: QueueItem[]) {
        try {
            await Axios.post<QueueItem[]>(`/IngestionQueue/`, queueItem);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.warn('IngestionQueue marked complete error: ', error);
        } finally {
            this.fetchQueue();
        }
    }

    protected patientDocWorkQueue: any[] = [];

    setPatientDocument(queueItem: any) {
        //Load up work queue
        if (Array.isArray(queueItem)) {
            this.patientDocWorkQueue = queueItem;
        } else {
            this.patientDocWorkQueue = [queueItem];
        }

        this.handlePatientDocSave();
    }

    async handlePatientDocSave() {
        if (!this.patientDocWorkQueue.length) {
            this.fetchQueue();
            return;
        }

        this.selectedQueueItem = this.patientDocWorkQueue.shift();
        this.$nextTick(() => {
            this.$bvModal.show('patient_documents_modal');
        });
    }

    processQueueItem(queueItem: QueueItem) {
        if (this.itemsSelected.length) {
            this.$store.queueItems = this.itemsSelected;
            const idx = this.$store.queueItems.findIndex(qi => qi.id == queueItem.id);
            this.$store.queueItems.splice(idx, 1);

            const lockTime = this.$store.queueItems.length * 1000 * 60 * 2; // 1000 ms/sec * 60 sec/min * 2 min

            this.$store.queueItems.forEach(qi => {
                this.addLockWithURL(`/IngestionQueue/${qi.id}/lock`, lockTime);
            });
        }
        this.addLockWithURL(`/IngestionQueue/${queueItem.id}/lock`, 60000)
            .then(_result => {
                // Build query object, only including properties with actual values
                const query = {};
                if (queueItem.imageID) query.imageID = String(queueItem.imageID);
                if (queueItem.escriptID) query.escriptID = String(queueItem.escriptID);
                if (queueItem.id) query.queueItemID = String(queueItem.id);
                if (queueItem.programTransferId) query.programTransfer = String(queueItem.programTransferId);

                this.$router.push({
                    name: 'Prescription',
                    query
                });
            })
            .catch(error => {
                if (error.response && error.response.status == 418) {
                    this.showLockedMessage(
                        EntityType.IngestionQueueItem,
                        queueItem?.id.toString(),
                        queueItem?.id.toString(),
                        error.response.data
                    );
                }
            });
    }

    toggleUrgent(item: any) {
        const newUrgent = !item.isUrgent;
        Axios.post(`/IngestionQueue/${item.id}/urgent`, newUrgent, { headers: { 'Content-Type': 'application/json' } })
            .then(() => {
                item.isUrgent = newUrgent;
            })
            .catch(() => {
                this.$notification(NotificationOptions.notificationOptionsPreset("Failed to update urgent status.", NotificationOptions.NotificationType.ERROR));
            });
    }

    formatLockExpires(value: Date) {
        return moment.utc(value).fromNow();
    }

    handleQueueRowStyles(item: any, type: any) {
        if (!item || type !== 'row') return;
        if (this.itemsSelected.some(i => i === item)) return "bg-light border border-info ingestion-item";
        if (item.isUrgent) return "table-danger ingestion-item";
        if (item.lockedBy) return "bg-warning locked ingestion-item";
        return "ingestion-item";
    }

    get selectViewOptions() {
        return [
            { text: "Incomplete", value: 'Incomplete' },
            { text: "Complete", value: 'Complete' }
        ];
    }

    get datePickerOptions() {
        return {
            disabledDate: (time: Date) => { // disable more than 7 days
                if (this.firstSelectedDate) {
                    const diffTime = Math.abs(time.getTime() - this.firstSelectedDate.getTime());
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays > 7;
                }
                return false;
            },
            onPick: (obj: any) => {   //to track the first selected date
                if (obj.minDate && !obj.maxDate) {
                    this.firstSelectedDate = obj.minDate;
                } else {
                    this.firstSelectedDate = null;
                }
            },
            shortcuts: [
                {
                    text: 'Today',
                    onClick: (picker: any) => {
                        const today = new Date();
                        picker.$emit('pick', [today, today]);
                        this.firstSelectedDate = null;
                    }
                },
                {
                    text: 'Last 7 days',
                    onClick: (picker: any) => {
                        const end = new Date();
                        const start = new Date();
                        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
                        picker.$emit('pick', [start, end]);
                        this.firstSelectedDate = null;
                    }
                },
            ]
        };
    }

    handleItemSelect(item: QueueItem, e?: boolean) {
        const hasItem = this.itemsSelected.some(i => i === item);
        this.itemsSelected = this.itemsSelected.filter(i => i !== item);

        if (e || (e === undefined && !hasItem)) {
            this.itemsSelected.push(item);
        }
    }

    handleQueueRowClicked(item: QueueItem) {
        this.handleItemSelect(item);
    }

    selectAllForPatients() {
        // Build a set of patient keys from all currently selected items
        const patientKeys = new Set<string>();
        let hasInvalidPatients = false;

        this.itemsSelected.forEach(item => {
            if (item.patient?.firstName && item.patient?.lastName && item.patient?.dateOfBirth) {
                const patientKey = `${item.patient.firstName.toLowerCase().trim()}_${item.patient.lastName.toLowerCase().trim()}_${item.patient.dateOfBirth}`;
                patientKeys.add(patientKey);
            } else {
                hasInvalidPatients = true;
            }
        });

        if (hasInvalidPatients) {
            this.$notification({
                type: 'warning',
                title: 'Some items skipped',
                message: 'Some selected items do not have complete patient information (firstname, lastname, and date of birth).'
            });
        }

        if (patientKeys.size === 0) {
            this.$notification({
                type: 'warning',
                title: 'No valid patients',
                message: 'No selected items have complete patient information.'
            });
            return;
        }

        // Select all items in the current table view that belong to any of these patients
        this.tableItems.forEach(item => {
            if (item.patient?.firstName && item.patient?.lastName && item.patient?.dateOfBirth) {
                const itemPatientKey = `${item.patient.firstName.toLowerCase().trim()}_${item.patient.lastName.toLowerCase().trim()}_${item.patient.dateOfBirth}`;

                if (patientKeys.has(itemPatientKey)) {
                    const hasItem = this.itemsSelected.some(i => i === item);
                    if (!hasItem) {
                        this.itemsSelected.push(item);
                    }
                }
            }
        });
    }

}
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
::v-deep table#escript-queue .flip-list-move {
    transition: transform .5s;
}

/*noinspection CssUnusedSymbol*/

::v-deep .selected-col {
    width: 2rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .button-col {
    width: 10rem;
    padding-top: 0;
    padding-bottom: 0;
}

::v-deep .button-col .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .date-col {
    width: 10rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .type-col {
    width: 11rem;
}

/*noinspection CssUnusedSymbol*/
::v-deep .ingestion-item {
    min-height: 20rem;
    max-height: 40rem;
    transition: all 0.3s;
    cursor: pointer;
}

::v-deep(.locked),
::v-deep(.completed) {
    width: 200px;
}

.action-bar {
    padding-top: 1px;
    position: sticky;
    top: 0;
    z-index: 600;
    border-bottom: 1px solid rgba(0, 0, 0, .05);
    box-shadow: inset 0px -7px 6px -6px rgba(0, 0, 0, .1);
}

::v-deep .image-col {
    width: 35%;
}

::v-deep .form-check .form-check-sign::before {
    background-color: #fff;
}

</style>
